import { RematchDispatch, createModel } from '@rematch/core';
import { message } from 'antd';
import axios from 'axios';
import produce from 'immer';
import { DEFAULT_PAGINATION, FILE_NAME_TYPE } from '../../commons/constants/constants';
import { PageRequest, PaginationType } from '../../types/CommonTypes';
import { storeName as entityStore } from '../entities/poiTemplate.entity';
import { storeName as userStore } from './user.model';

import { uploadFiles } from '../../services/uploader.service';
import { RootModel } from '../models';

export const storeName = 'poiTemplates';

export interface initialValuesType {
  ids: (number | string)[];
  pagination: PaginationType | null;
  criteria: { [key: string]: any };
  loading: boolean;
  editId: any;
  visible: boolean;
  selectedTemplate: any;
}

const initialState: initialValuesType = {
  ids: [],
  pagination: DEFAULT_PAGINATION,
  criteria: {},
  loading: false,
  editId: null,
  visible: false,
  selectedTemplate: '',
};

export const poiTemplates: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    setLoading: produce((state, payload) => {
      state.loading = payload;
    }),
    setVisible: produce((state, payload) => {
      state.visible = payload;
    }),
    setEditId: produce((state, payload) => {
      state.editId = payload;
    }),
    setSelectedTemplate: produce((state, payload) => {
      state.selectedTemplate = payload || '';
    }),
  },
  effects: (dispatch: RematchDispatch<typeof poiTemplates>) => ({
    async reloadList(payload, state) {
      const { criteria, pagination } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria });
    },
    async resetFilter(payload, state) {
      const { pagination } = state[storeName];
      await this.filterAsync({ ...pagination, page: 0 });
    },
    async pagingAsync(payload: PageRequest, state) {
      const { criteria = {}, pagination = {} } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria, ...payload });
    },
    async filterAsync(params, state) {
      this.setLoading(true);
      try {
        const data = await dispatch[entityStore].load(params);
        if (data && data.content) {
          this.setIds(data.content.map((tour) => tour.id));
          this.setPagination((({ content, ...otherProps }) => otherProps)(data));
          this.setCriteria(params);
        }

        return data;
      } catch (e) {
        console.error(e);
        message.error('Failed to filter poi templates');
      } finally {
        this.setLoading(false);
      }
    },
    async deleteAsync(payload, state) {
      this.setLoading(true);
      try {
        await dispatch[entityStore].delete(payload.id);
        await this.reloadList();
        message.success(`Item ${payload.name} deleted`);
      } catch (e) {
        console.error(e);
        message.error(`Failed to delete ${payload.name}`);
      } finally {
        this.setLoading(false);
      }
    },
    async createPoiTemplate(payload, state) {
      this.setLoading(true);
      try {
        const { user } = state[userStore];
        const uploadActions: any[] = [];

        if (payload?.image) {
          const promise = uploadFiles(
            payload?.id || null,
            [payload.image],
            FILE_NAME_TYPE.IMAGE,
            user.id,
            'image'
          ).then((result) => {
            const { filesList } = result;
            if (filesList.length) {
              const firstFile = filesList[0];
              payload.image = { id: firstFile.id, url: firstFile.url };
            }
          });
          uploadActions.push(promise);
        }

        if (payload?.video) {
          const promiseVideo = uploadFiles(
            payload?.id || null,
            [payload?.video],
            FILE_NAME_TYPE.VIDEO,
            user.id,
            'video'
          ).then((result) => {
            const { filesList } = result;
            if (filesList.length) {
              const firstFile = filesList[0];
              payload.video = { id: firstFile.id, url: firstFile.url };
            }
          });
          uploadActions.push(promiseVideo);
        }

        if (payload?.sound) {
          const promiseSound = uploadFiles(
            payload?.id || null,
            [payload?.sound],
            FILE_NAME_TYPE.AUDIO,
            user.id,
            'sound'
          ).then((result) => {
            const { filesList } = result;
            if (filesList.length) {
              const firstFile = filesList[0];
              payload.sound = { id: firstFile.id, url: firstFile.url };
            }
          });
          uploadActions.push(promiseSound);
        }

        await axios.all(uploadActions);
        await dispatch[entityStore].createPoiTemplate(payload);
        this.reloadList();
        message.success('Success to create poi template');
      } catch (e) {
        console.error(e);
        message.error(`Fail to create poi template`);
      } finally {
        this.setLoading(false);
      }
    },
    async updatePoiTemplate(payload, state) {
      this.setLoading(true);
      try {
        await dispatch[entityStore].updatePoiTemplate(payload);
        this.reloadList();
        message.success('Success to update poi template');
      } catch (e) {
        console.error(e);
        message.error(`Fail to update poi template`);
      } finally {
        this.setLoading(false);
      }
    },
    async editAsync(payload, state) {
      this.setLoading(true);
      try {
        const item = await dispatch[entityStore].findById(payload);
        this.setEditId(item.id);
        this.setVisible(true);
      } catch (e) {
        console.error(e);
        message.error(`Failed to get ${payload}`);
      } finally {
        this.setLoading(false);
      }
    },
  }),
});

export default poiTemplates;
